import moment from 'moment';

export const flatten = menu =>
  menu.length
    ? menu.reduce((acc, m) => {
      if (m.children && m.children.length) {
        acc = acc.concat(flatten(m.children)); // eslint-disable-line no-param-reassign
      } else {
        acc.push(m);
      }
      return acc;
    }, [])
    : [];

export const getFromImmutable = (immutableVar, name, defaultVal) =>
  (immutableVar &&
    immutableVar.getIn(['data', name]) &&
    immutableVar.getIn(['data', name]).toJS()) ||
  defaultVal;

export const getImmutableData = (immutableVar, defaultVal) =>
  (immutableVar &&
    immutableVar.get('data') &&
    immutableVar.get('data').toJS()) ||
  defaultVal;

export const getSelectOptions = data =>
  data.map(option => ({
    label: `${option.codeName} (${option.codeDescription})`,
    value: option.codeName,
  }));

export const getRandId = () =>
  Math.random()
    .toFixed(36)
    .substring(2, 15) +
  Math.random()
    .toFixed(36)
    .substring(2, 15);

export const PERMIT_TABS = Object.freeze({
  LISTING_PAGE: 1,
  CREATE_PAGE: 2,
  EDIT_PAGE: 3,
});

export const setNumberWithCommas = x =>
  typeof x === 'number'
    ? x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')
    : '0';

const replaceCommas = x => x.replace(/,/g, '');

export const setNumberWithoutCommas = x =>
  Number.isNaN(Number(replaceCommas(x)))
    ? Number(replaceCommas(x).slice(0, -1))
    : Number(replaceCommas(x));

export const getSearchItemsByGroup = ({ fields, groupName }) =>
  fields
    .filter(field => field.grouping === groupName)
    .sort((a, b) => a.order - b.order);

export const getFormattedBirthDate = birthDate =>
  birthDate
    ? birthDate
      .split(' ')[0]
      .split('-')
      .reduce((acc, item, idx) => {
        switch (idx) {
          case 0:
            acc[2] = item;
            return acc;
          case 1:
            acc[0] = item;
            return acc;
          case 2:
            acc[1] = item;
            return acc;
          default:
            return acc;
        }
      }, [])
      .join('/')
    : '';

export const getValidProrateDate = date =>
  date
    .reduce((acc, item, idx) => {
      switch (idx) {
        case 0:
          acc[1] = item;
          return acc;
        case 1:
          acc[2] = item;
          return acc;
        case 2:
          acc[0] = item;
          return acc;
        default:
          return acc;
      }
    }, [])
    .join('-');

export const getCodeDescCodefiles = codefiles =>
  codefiles.map(({ label, value }) => ({
    label: `${value}${label ? ` (${label})` : ''}`,
    value,
  }));

export const VIEW_PLATS_PAGES = Object.freeze({
  LISTING_PAGE: 'LISTING_PAGE',
  DETAIL_PAGE: 'DETAIL_PAGE',
});

export const getFileExtensionFromSignedUrl = url => {
  if (!url) return;
  const fileUrl = url.slice(0, url.indexOf('?'));
  return fileUrl.slice(fileUrl.lastIndexOf('.') + 1);
};

export const economicUnitsDropdowns = [
  {
    fieldName: 'Property Use',
    fieldType: 'Property',
  },
  {
    fieldName: 'Category',
    fieldType: 'Property',
  },
  {
    fieldName: 'Class',
    fieldType: 'Income',
  },
];

export const getDropdownData = dropdowns =>
  dropdowns
    ? [
      {
        value: null,
        label: '- No Value -',
      },
      ...dropdowns.map(({ codeName, codeDescription }) => ({
        value: codeName,
        label: `${codeName} - ${codeDescription ? `(${codeDescription})` : ''
          }`,
      })),
    ]
    : [];

export const suppressArrowKeys = params => {
  const KEY_ENTER = 13;
  const KEY_LEFT = 37;
  const KEY_UP = 38;
  const KEY_RIGHT = 39;
  const KEY_DOWN = 40;
  const event = params.event;
  const key = event.which;
  const keysToSuppress = [KEY_ENTER, KEY_LEFT, KEY_UP, KEY_RIGHT, KEY_DOWN];
  let suppress = false;
  if (params.editing) {
    suppress = keysToSuppress.indexOf(key) >= 0;
  }
  return suppress;
};

/**
 * Convert error description from BPP Import errors
 * from: " ; taxingUnitCode: C12 is invalid.  ; taxingUnitCode: G01 is invalid. "
 * to: "Taxing Unit Code: C12 is invalid. Taxing Unit Code: G01 is invalid."
 */
export const convertErrorDescription = description =>
  description
    ? description
      .slice(3)
      .split('.  ; ')
      .map(err => {
        const [desc, error] = err.split(':');
        const descWithSpace = desc.replace(/([A-Z])/g, ' $1');
        return `${descWithSpace[0].toUpperCase()}${descWithSpace.slice(
          1,
        )}:${error}`;
      })
      .join('. ')
    : description;

/**
 * from: "2020-02-29 13:53:32"
 * to: "02-29-2020 | 01:53pm"
 */
export const getFormattedDateFromImportError = date =>
  date ? moment(date).format('MM-DD-YYYY | hh:mma') : '';

export const savePropertyHistory = (
  propId,
  userId,
  preference,
  persistPreference,
) => {
  const preferenceData = preference.get('data').toJS();
  if (
    preferenceData.appraisalPropertyHistory &&
    preferenceData.appraisalPropertyHistory.length > 0
  ) {
    preferenceData.appraisalPropertyHistory = [propId].concat(
      preferenceData.appraisalPropertyHistory.filter(id => id !== propId),
    );
    if (preferenceData.appraisalPropertyHistory.length > 20) {
      preferenceData.appraisalPropertyHistory.pop();
    }
  } else {
    preferenceData.appraisalPropertyHistory = [propId];
  }
  persistPreference({
    userId,
    data: {
      preference: preferenceData,
    },
  });
};

export const savePIDsInputHistory = (
  propIdsStr,
  userId,
  preference,
  persistPreference,
  key,
) => {
  const preferenceData = preference.get('data').toJS();
  if (preferenceData[key] && preferenceData[key].length > 0) {
    preferenceData[key] = [propIdsStr].concat(
      preferenceData[key].slice(0, 99).filter(id => id !== propIdsStr),
    );
  } else {
    preferenceData[key] = [propIdsStr];
  }
  persistPreference({
    userId,
    data: {
      preference: preferenceData,
    },
  });
};
